import { type Component, createSignal, createEffect, onMount, onCleanup, For, Show } from 'solid-js';
import { useTranslation } from '@/i18n';
import { chatStore, type ChatMessage } from '@/stores/chatStore';

/**
 * Compress an image file using Canvas API
 * Reduces image dimensions and quality for efficient upload
 */
async function compressImage(file: File, maxWidth = 1200, maxHeight = 1200, quality = 0.8): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Canvas not supported'));
      return;
    }

    img.onload = () => {
      // Calculate new dimensions maintaining aspect ratio
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }

      // Set canvas size
      canvas.width = width;
      canvas.height = height;

      // Draw image with white background (for transparency)
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Compression failed'));
            return;
          }

          // Create new File with compressed data
          const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });

          console.log(`Image compressed: ${(file.size / 1024).toFixed(0)}KB → ${(compressedFile.size / 1024).toFixed(0)}KB`);
          resolve(compressedFile);
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    // Load image from file
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * TeamChat - WhatsApp-style chat component for team-operator communication
 *
 * Features:
 * - Message bubbles (green for team, gray for operator)
 * - Timestamps
 * - Image preview with lightbox
 * - Input field with send button
 * - Image attachment button
 * - Auto-scroll to newest message
 */
const TeamChat: Component = () => {
  const { t } = useTranslation();
  const [message, setMessage] = createSignal('');
  const [selectedImage, setSelectedImage] = createSignal<File | null>(null);
  const [imagePreview, setImagePreview] = createSignal<string | null>(null);
  const [lightboxImage, setLightboxImage] = createSignal<string | null>(null);
  const [isCompressing, setIsCompressing] = createSignal(false);
  let messagesEndRef: HTMLDivElement | undefined;
  let fileInputRef: HTMLInputElement | undefined;

  // Load messages on mount and set up polling fallback
  let pollInterval: ReturnType<typeof setInterval> | undefined;

  onMount(async () => {
    // Set chat as open first
    chatStore.setChatOpen(true);

    // Load messages and then mark as read
    await chatStore.loadMessages();

    // Always mark as read when opening chat (regardless of unreadCount state)
    chatStore.markAsRead();

    // Polling fallback every 5 seconds (in case WebSocket doesn't work)
    pollInterval = setInterval(() => {
      chatStore.loadMessages();
    }, 5000);
  });

  // Close chat and cleanup on unmount
  onCleanup(() => {
    chatStore.setChatOpen(false);
    if (pollInterval) {
      clearInterval(pollInterval);
    }
  });

  // Auto-scroll to bottom when messages change
  createEffect(() => {
    const messages = chatStore.state.messages;
    if (messages.length > 0 && messagesEndRef) {
      setTimeout(() => {
        messagesEndRef?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  });

  // Format timestamp
  const formatTime = (isoString: string): string => {
    const date = new Date(isoString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    const timeStr = date.toLocaleTimeString('pl-PL', {
      hour: '2-digit',
      minute: '2-digit'
    });

    if (isToday) {
      return timeStr;
    } else if (isYesterday) {
      return `${t('chat.yesterday')} ${timeStr}`;
    } else {
      return date.toLocaleDateString('pl-PL', {
        day: '2-digit',
        month: '2-digit'
      }) + ' ' + timeStr;
    }
  };

  // Handle image selection with automatic compression
  const handleImageSelect = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];

    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert(t('chat.invalidImageFormat'));
      return;
    }

    // Max size after compression (5MB) - we'll try to compress larger files
    const maxFinalSize = 5 * 1024 * 1024;
    // Threshold for compression (500KB) - compress if larger
    const compressionThreshold = 500 * 1024;

    let processedFile = file;

    // Compress if file is larger than threshold
    if (file.size > compressionThreshold) {
      setIsCompressing(true);
      try {
        // Try different quality levels if needed
        let quality = 0.8;
        processedFile = await compressImage(file, 1200, 1200, quality);

        // If still too large, try lower quality
        if (processedFile.size > maxFinalSize && quality > 0.5) {
          quality = 0.6;
          processedFile = await compressImage(file, 1000, 1000, quality);
        }

        // Last resort - even lower quality
        if (processedFile.size > maxFinalSize && quality > 0.3) {
          quality = 0.4;
          processedFile = await compressImage(file, 800, 800, quality);
        }
      } catch (error) {
        console.error('Compression failed:', error);
        // Fall back to original file
        processedFile = file;
      } finally {
        setIsCompressing(false);
      }
    }

    // Final size check
    if (processedFile.size > maxFinalSize) {
      alert(t('chat.imageTooLarge'));
      return;
    }

    setSelectedImage(processedFile);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(processedFile);
  };

  // Clear selected image
  const clearImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef) {
      fileInputRef.value = '';
    }
  };

  // Send message
  const handleSend = async () => {
    const text = message().trim();
    const image = selectedImage();

    if (!text && !image) return;

    const success = await chatStore.sendMessage(text, image || undefined);
    if (success) {
      setMessage('');
      clearImage();
    }
  };

  // Handle Enter key
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div class="flex flex-col h-full max-h-[70vh] min-h-[400px]">
      {/* Header */}
      <div class="flex items-center gap-3 p-4 border-b border-white/10 bg-white/5">
        <div class="w-10 h-10 rounded-full bg-emerald-500/30 flex items-center justify-center">
          <span class="text-lg">🎮</span>
        </div>
        <div>
          <div class="font-semibold text-white">{t('chat.operator')}</div>
          <div class="text-xs text-white/50">{t('chat.title')}</div>
        </div>
      </div>

      {/* Messages area */}
      <div class="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-black/20 to-black/10">
        <Show when={chatStore.state.isLoading}>
          <div class="flex justify-center py-8">
            <div class="animate-spin w-8 h-8 border-2 border-white/30 border-t-emerald-500 rounded-full" />
          </div>
        </Show>

        <Show when={!chatStore.state.isLoading && chatStore.state.messages.length === 0}>
          <div class="text-center py-8 text-white/40">
            <div class="text-4xl mb-2">💬</div>
            <p>{t('chat.noMessages')}</p>
          </div>
        </Show>

        <For each={chatStore.state.messages}>
          {(msg) => (
            <div
              class={`flex ${msg.sender_type === 'team' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                class={`max-w-[80%] rounded-2xl px-4 py-2 ${
                  msg.sender_type === 'team'
                    ? 'bg-emerald-600 text-white rounded-br-md'
                    : 'bg-white/10 text-white/90 rounded-bl-md'
                }`}
              >
                {/* Image if present */}
                <Show when={msg.image_url}>
                  <div
                    class="mb-2 cursor-pointer"
                    onClick={() => setLightboxImage(msg.image_url)}
                  >
                    <img
                      src={msg.image_url!}
                      alt="Attached image"
                      class="max-w-full max-h-48 rounded-lg"
                      loading="lazy"
                    />
                  </div>
                </Show>

                {/* Text content */}
                <Show when={msg.content}>
                  <p class="whitespace-pre-wrap break-words">{msg.content}</p>
                </Show>

                {/* Timestamp */}
                <div
                  class={`text-[10px] mt-1 ${
                    msg.sender_type === 'team' ? 'text-white/70' : 'text-white/40'
                  }`}
                >
                  {formatTime(msg.created_at)}
                  <Show when={msg.sender_type === 'team'}>
                    <span class="ml-1">{msg.is_read ? '✓✓' : '✓'}</span>
                  </Show>
                </div>
              </div>
            </div>
          )}
        </For>

        <div ref={messagesEndRef} />
      </div>

      {/* Image preview */}
      <Show when={imagePreview()}>
        <div class="px-4 py-2 bg-white/5 border-t border-white/10">
          <div class="relative inline-block">
            <img
              src={imagePreview()!}
              alt="Preview"
              class="h-20 rounded-lg"
            />
            <button
              onClick={clearImage}
              class="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center hover:bg-red-600 transition-colors"
            >
              ✕
            </button>
          </div>
        </div>
      </Show>

      {/* Input area */}
      <div class="p-4 border-t border-white/10 bg-white/5">
        {/* Text input - full width on top */}
        <div class="mb-3">
          <textarea
            value={message()}
            onInput={(e) => setMessage(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chat.typeMessage')}
            rows={2}
            class="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 resize-none focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/30 transition-all text-base"
            style={{ "min-height": "60px", "max-height": "150px" }}
          />
        </div>

        {/* Action buttons row */}
        <div class="flex items-center justify-between gap-3">
          {/* Left side - attachment button */}
          <button
            onClick={() => fileInputRef?.click()}
            disabled={isCompressing()}
            class="h-11 px-4 rounded-xl bg-white/10 flex items-center justify-center gap-2 text-white/70 hover:bg-white/20 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-wait"
            title={t('chat.attachImage')}
          >
            <Show
              when={!isCompressing()}
              fallback={
                <div class="animate-spin w-5 h-5 border-2 border-white/30 border-t-white rounded-full" />
              }
            >
              <>
                <span class="text-lg">📷</span>
                <span class="text-sm hidden sm:inline">{t('chat.attachImage')}</span>
              </>
            </Show>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            class="hidden"
            onChange={handleImageSelect}
          />

          {/* Right side - send button */}
          <button
            onClick={handleSend}
            disabled={chatStore.isSending() || (!message().trim() && !selectedImage())}
            class="h-11 px-6 rounded-xl bg-emerald-500 flex items-center justify-center gap-2 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-emerald-600 transition-colors"
            title={t('chat.send')}
          >
            <Show
              when={!chatStore.isSending()}
              fallback={
                <div class="animate-spin w-5 h-5 border-2 border-white/30 border-t-white rounded-full" />
              }
            >
              <>
                <span>{t('chat.send')}</span>
                <span class="text-lg">➤</span>
              </>
            </Show>
          </button>
        </div>
      </div>

      {/* Lightbox for images */}
      <Show when={lightboxImage()}>
        <div
          class="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          {/* Close button */}
          <button
            class="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white text-2xl hover:bg-white/20 transition-colors"
            onClick={() => setLightboxImage(null)}
          >
            ✕
          </button>

          {/* Full size image */}
          <img
            src={lightboxImage()!}
            alt="Full size"
            class="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Hint to close */}
          <p class="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/50 text-sm">
            {t('teamsProgress.tapToClose')}
          </p>
        </div>
      </Show>
    </div>
  );
};

export default TeamChat;
